import checkmateApi from '../axios/checkmateApi';

export type SearchJobStatus = 'PROCESSING' | 'PENDING' | 'DONE' | 'FAILED' | 'CANCELLED' | 'KILLED' | 'KILLING';
export type OperationalStatus = 'ACTIVE' | 'IDLE' | 'DEGRADED' | 'ERROR';

export interface ActiveSearchEntry {
    id: string;
    kind: 'SEARCH' | 'REPORT';
    status: SearchJobStatus;
    progress: number;
    name?: string;
    updatedAt: number;
    stale: boolean;
    cacheResolution?: string;
}

export interface SystemServiceEntry {
    serviceName: string;
    apiConsumption?: number;
    recordsProcessed?: number;
    recordsFailed?: number;
    kafkaPublishSuccess?: number;
    kafkaPublishFailed?: number;
    dataSourceError?: number;
    processingTimeMs: number[];
    gauges?: Record<string, number | null>;
    errors: string[];
    lastUpdated: string;
    pipelineKey: string;
    active: boolean;
    refreshType: string;
    lastRefreshedAt: number;
    refreshIntervalDays: number;
    tier?: string;
    stale: boolean;
}

export interface DataSource {
    name: string;
    displayName: string;
    operationalStatus: OperationalStatus;
    records24h: number | null;
    consumerLag: number | null;
    lastSeen: number | null;
}

export interface StatusSummaryResponse {
    activeCount: number;
    failedCount: number;
    completedCount: number;
    activeEntries: ActiveSearchEntry[];
    lastUpdatedAt: number;
    systemStatus: Record<string, SystemServiceEntry>;
    dataSources?: DataSource[];
}

export const getStatusSummary = async (signal?: AbortSignal): Promise<{ data: StatusSummaryResponse }> => {
    try {
        const response = await checkmateApi({
            method: 'GET',
            url: 'status/summary',
            signal,
            timeout: 30_000,
        });
        return response;
    } catch (err: any) {
        const raw = err?.response?.headers?.['retry-after'];
        const n = Number(raw);
        const retryAfterMs = raw != null && String(raw).trim() !== '' && Number.isInteger(n) && n >= 0 ? n * 1000 : null;
        // Attach retryAfterMs via a shallow clone rather than mutating the
        // caught axios error in place, since that error object may be
        // referenced elsewhere (e.g. interceptors, logging). `message` and
        // `stack` are non-enumerable on Error instances, so copy them explicitly.
        const e: any = Object.assign(Object.create(Object.getPrototypeOf(err)), err, {
            message: err?.message,
            stack: err?.stack,
            retryAfterMs,
        });
        throw e;
    }
};

export const cancelSearch = async (id: string): Promise<void> => {
    await checkmateApi({
        method: 'POST',
        url: `search/kill/${id}`,
    });
};
